import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  ApiResponse,
  PagedResult,
  ProductDto,
  ProductLiteDto,
  CreateProductRequest,
  UpdateProductRequest,
  WeightCodeResult,
  ProductsStockSummaryDto,
} from '../models/product.models';
import {
  ProductVariantMatrixDto,
  CreateProductWithVariantsRequest,
  ManageVariantsRequest,
} from '../models/variant.models';
import {
  ExcelPreview,
  ScannedProductRow,
  ProductImportMapping,
  ProductImportResult,
} from '../models/admin.models';
import { ApiService } from './api.service';

@Injectable({ providedIn: 'root' })
export class ProductService extends ApiService {
  private readonly endpoint = `${environment.apiUrl}/api/Products`;

  getAllLite(storeId?: string | null): Observable<ApiResponse<ProductLiteDto[]>> {
    let params = new HttpParams();
    if (storeId) params = params.set('storeId', storeId);
    return this.http.get<ApiResponse<ProductLiteDto[]>>(`${this.endpoint}/all`, { params });
  }

  getProducts(
    page: number,
    pageSize: number,
    search?: string,
    storeIds?: string[],
    categoryId?: string | null,
  ): Observable<ApiResponse<PagedResult<ProductDto>>> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    if (search) params = params.set('search', search);
    if (storeIds?.length) storeIds.forEach((id) => (params = params.append('storeIds', id)));
    if (categoryId) params = params.set('categoryId', categoryId);
    return this.http.get<ApiResponse<PagedResult<ProductDto>>>(this.endpoint, { params });
  }

  /**
   * Ro'yxat ustidagi jamlanma kartochkalar. Filtrlar `getProducts` bilan aynan bir xil
   * uzatilishi kerak — aks holda kartochkadagi raqam pastdagi ro'yxatga mos kelmaydi.
   */
  getStockSummary(
    search?: string,
    storeIds?: string[],
    /** Jamlanma bitta valyutada bo'lishi shart — aks holda summa ma'nosiz. */
    currency?: string,
  ): Observable<ApiResponse<ProductsStockSummaryDto>> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (storeIds?.length) storeIds.forEach((id) => (params = params.append('storeIds', id)));
    if (currency) params = params.set('currency', currency);
    return this.http.get<ApiResponse<ProductsStockSummaryDto>>(`${this.endpoint}/stock-summary`, {
      params,
    });
  }

  getByBarcode(barcode: string): Observable<ProductDto | null> {
    return this.getProducts(1, 1, barcode).pipe(map((res) => res.data?.items?.[0] ?? null));
  }

  /**
   * Savdo vaqtida barcode skanerlash — WeightCode ham qo'llab-quvvatlanadi.
   *
   * Topilmagan barkod backendda 404 qaytaradi, lekin bu KUTILGAN holat: kassir
   * begona yoki ro'yxatga olinmagan tovarni skanerlashi normal. Global qizil
   * "NOT_FOUND" toasti o'rniga `null` qaytaramiz — chaqiruvchi buni tushunarli
   * ogohlantirish qilib ko'rsatadi. Qolgan xatolar odatdagidek toast beradi;
   * `X-Skip-Error-Toast` interceptorni o'chirgani uchun uni shu yerda chiqaramiz.
   */
  getByBarcodeForSale(barcode: string, storeId: string): Observable<WeightCodeResult | null> {
    return this.http
      .get<ApiResponse<WeightCodeResult>>(`${this.endpoint}/barcode/${barcode}`, {
        params: { storeId },
        headers: { 'X-Skip-Error-Toast': '1' },
      })
      .pipe(
        map((res) => res.data ?? null),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404) return of(null);
          this.showError(this.extractError(err));
          return throwError(() => err);
        }),
      );
  }

  getById(id: string): Observable<ApiResponse<ProductDto>> {
    return this.http.get<ApiResponse<ProductDto>>(`${this.endpoint}/${id}`);
  }

  create(data: CreateProductRequest): Observable<ApiResponse<{ id: string }>> {
    return this.http.post<ApiResponse<{ id: string }>>(this.endpoint, data);
  }

  createWithVariants(
    data: CreateProductWithVariantsRequest,
  ): Observable<ApiResponse<{ id: string }>> {
    return this.http.post<ApiResponse<{ id: string }>>(`${this.endpoint}/with-variants`, data);
  }

  /** Variant tanlash matritsasi; storeId berilsa har bir variant qoldig'i bilan. */
  getVariantMatrix(id: string, storeId?: string): Observable<ApiResponse<ProductVariantMatrixDto>> {
    let params = new HttpParams();
    if (storeId) params = params.set('storeId', storeId);
    return this.http.get<ApiResponse<ProductVariantMatrixDto>>(
      `${this.endpoint}/${id}/variant-matrix`,
      { params },
    );
  }

  update(id: string, data: UpdateProductRequest): Observable<ApiResponse<unknown>> {
    return this.http.put<ApiResponse<unknown>>(`${this.endpoint}/${id}`, data);
  }

  /** Mavjud mahsulot variantlarini boshqarish (qo'shish/tahrirlash/o'chirish). */
  manageVariants(id: string, data: ManageVariantsRequest): Observable<ApiResponse<unknown>> {
    return this.http.put<ApiResponse<unknown>>(`${this.endpoint}/${id}/variants`, data);
  }

  generateBarcode(): Observable<string> {
    return this.http
      .get<ApiResponse<{ barcode: string }>>(`${this.endpoint}/generate-barcode`)
      .pipe(map((res) => res.data.barcode));
  }

  delete(id: string): Observable<ApiResponse<unknown>> {
    return this.http.delete<ApiResponse<unknown>>(`${this.endpoint}/${id}`);
  }

  uploadImage(id: string, file: File): Observable<ApiResponse<{ imageUrl: string }>> {
    const form = new FormData();
    form.append('file', file, file.name);
    // X-Skip-Error-Toast: image upload errors are handled inline (warn toast), not via global interceptor
    return this.http.put<ApiResponse<{ imageUrl: string }>>(`${this.endpoint}/${id}/image`, form, {
      headers: { 'X-Skip-Error-Toast': '1' },
    });
  }

  deleteImage(id: string): Observable<ApiResponse<unknown>> {
    return this.http.delete<ApiResponse<unknown>>(`${this.endpoint}/${id}/image`);
  }

  // ----- Excel'dan ommaviy import -----
  //
  // Admin panelidagi bilan bir xil ikki bosqichli oqim, farqi — tenant so'rovda
  // uzatilmaydi, backend uni JWT dan oladi.

  /** 1-bosqich: fayl ustunlari va namuna qatorlar. */
  previewImport(file: File): Observable<ApiResponse<ExcelPreview>> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<ApiResponse<ExcelPreview>>(`${this.endpoint}/import/preview`, form);
  }

  /** 2-bosqich: moslashtirish bo'yicha mahsulotlarni yaratish. */
  importProducts(
    file: File,
    mapping: ProductImportMapping,
    storeId: string | null,
    currencies?: { sellCurrency: string | null; purchaseCurrency: string | null },
  ): Observable<ApiResponse<ProductImportResult>> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('hasHeaderRow', String(mapping.hasHeaderRow));
    form.append('nameColumn', String(mapping.nameColumn ?? 0));
    form.append('sellPriceColumn', String(mapping.sellPriceColumn ?? 0));
    if (storeId) form.append('storeId', storeId);

    // Valyuta faqat tanlangan bo'lsa yuboriladi — bo'sh yuborilsa backend uni
    // kod deb o'qishga urinib, "valyuta topilmadi" xatosini berardi.
    if (currencies?.sellCurrency) form.append('sellCurrency', currencies.sellCurrency);
    if (currencies?.purchaseCurrency) form.append('purchaseCurrency', currencies.purchaseCurrency);

    // Ixtiyoriy ustunlar faqat belgilanganda yuboriladi — `null` yuborilsa backend
    // uni 0-ustun deb o'qib, barcha mahsulotga birinchi ustunni yozib qo'yardi.
    const optional: [keyof ProductImportMapping, string][] = [
      ['brandColumn', 'brandColumn'],
      ['barcodeColumn', 'barcodeColumn'],
      ['purchasePriceColumn', 'purchasePriceColumn'],
      ['categoryColumn', 'categoryColumn'],
      ['unitColumn', 'unitColumn'],
      ['isWeightColumn', 'isWeightColumn'],
      ['quantityColumn', 'quantityColumn'],
    ];
    for (const [key, field] of optional) {
      const value = mapping[key];
      if (value != null) form.append(field, String(value));
    }

    return this.http.post<ApiResponse<ProductImportResult>>(`${this.endpoint}/import`, form);
  }

  /**
   * Rasmdan (AI) mahsulot qatorlarini o'qish — faqat TAKLIF, bazaga hech narsa
   * yozilmaydi. Natija kirim formasida mavjud mahsulotlarga bog'lanadi va o'sha
   * yerdan saqlanadi.
   */
  scanImport(file: File): Observable<ApiResponse<ScannedProductRow[]>> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<ApiResponse<ScannedProductRow[]>>(`${this.endpoint}/import/scan`, form);
  }
}
